import React from 'react';
import Helmet from 'react-helmet';
import {Inertia} from '@inertiajs/inertia';
import {InertiaLink, useForm, usePage} from '@inertiajs/inertia-react';
import Layout from '@/Shared/Layout';
import DeleteButton from '@/Shared/DeleteButton';
import LoadingButton from '@/Shared/LoadingButton';
import TextInput from '@/Shared/TextInput';
import SelectInput from '@/Shared/SelectInput';
import FileInput from '@/Shared/FileInput';
import TrashedMessage from '@/Shared/TrashedMessage';
import PermissionsGate from "@/Shared/PermissionsGate";

const Edit = () => {
  const { adminUser} = usePage().props;

  const { data, setData, errors, post, processing } = useForm({
    first_name: adminUser.first_name || '',
    last_name: adminUser.last_name || '',
    email: adminUser.email || '',
    password: adminUser.password || '',
    admin_role_id: adminUser.admin_role_id || '',
    photo: '',

    // NOTE: When working with Laravel PUT/PATCH requests and FormData
    // you SHOULD send POST request and fake the PUT request like this.
    _method: 'POST'
  });

  function handleSubmit(e) {
    e.preventDefault();
    post(route('admin-users.update', adminUser.id).url());
  }

  function destroy() {
    if (confirm('Are you sure you want to delete this user?')) {
      Inertia.delete(route('admin-users.destroy', adminUser.id));
    }
  }

  function restore() {
    if (confirm('Are you sure you want to restore this user?')) {
      Inertia.put(route('admin-users.restore', adminUser.id).url());
    }
  }

  return (
    <div>
      <PermissionsGate scopes={['create-user']}/>
      <Helmet title={`${data.first_name} ${data.last_name}`} />
      <div className="flex justify-start max-w-lg mb-8">
        <h1 className="text-3xl font-bold">
          <InertiaLink
            href={route('admin-users')}
            className="text-indigo-600 hover:text-indigo-700"
          >
            Admin-Users
          </InertiaLink>
          <span className="mx-2 font-medium text-indigo-600">/</span>
          {data.first_name} {data.last_name}
        </h1>
        {adminUser.photo && (
          <img className="block w-8 h-8 ml-4 rounded-full" src={adminUser.photo} />
        )}
      </div>
      {adminUser.deleted_at && (
        <TrashedMessage onRestore={restore}>
          This user has been deleted.
        </TrashedMessage>
      )}
      <div className="max-w-3xl overflow-hidden bg-white rounded shadow">
        <form onSubmit={handleSubmit}>
          <div className="flex flex-wrap p-8 -mb-8 -mr-6">
            <TextInput
              className="w-full pb-8 pr-6 lg:w-1/2"
              label="First Name"
              name="first_name"
              errors={errors.first_name}
              value={data.first_name}
              onChange={e => setData('first_name', e.target.value)}
            />
            <TextInput
              className="w-full pb-8 pr-6 lg:w-1/2"
              label="Last Name"
              name="last_name"
              errors={errors.last_name}
              value={data.last_name}
              onChange={e => setData('last_name', e.target.value)}
            />
            <TextInput
              className="w-full pb-8 pr-6 lg:w-1/2"
              label="Email"
              name="email"
              type="email"
              errors={errors.email}
              value={data.email}
              onChange={e => setData('email', e.target.value)}
            />
            <TextInput
              className="w-full pb-8 pr-6 lg:w-1/2"
              label="Password"
              name="password"
              type="password"
              errors={errors.password}
              value={data.password}
              onChange={e => setData('password', e.target.value)}
            />
            <SelectInput
              className="w-full pb-8 pr-6 lg:w-1/2"
              label="Role"
              name="role"
              errors={errors.admin_role_id}
              value={data.admin_role_id}
              onChange={e => setData('admin_role_id', e.target.value)}
            >
              <PermissionsGate scopes={['update-super_admin']}><option value="1">Super Admin</option></PermissionsGate>
              <PermissionsGate scopes={['update-admin']}><option value="2">Admin</option></PermissionsGate>
              <PermissionsGate scopes={['update-user']}><option value="3">User</option></PermissionsGate>
              <PermissionsGate scopes={['update-owner']}><option value="4">Owner</option></PermissionsGate>
            </SelectInput>
            <FileInput
              className="w-full pb-8 pr-6 lg:w-1/2"
              label="Photo"
              name="photo"
              accept="image/*"
              errors={errors.photo}
              value={data.photo}
              onChange={photo => setData('photo', photo)}
            />
          </div>
          <div className="flex items-center px-8 py-4 bg-gray-100 border-t border-gray-200">
            {!adminUser.deleted_at && (
              <DeleteButton onDelete={destroy}>Delete User</DeleteButton>
            )}
            <LoadingButton
              loading={processing}
              type="submit"
              className="ml-auto btn-default"
            >
              Update User
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
};

Edit.layout = page => <Layout children={page} />;

export default Edit;
